import React, { CSSProperties, useRef } from "react";
import {
  Row,
  Col,
  Layout,
  Typography,
  Form,
  Spin,
  Drawer,
  Grid,
  Radio,
  message,
  Collapse,
  CollapseProps,
  Select,
  Menu,
  Dropdown,
  Button,
} from "antd";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  // getDataNull,
  getNewsDataSuccess,
  getReGenerateArticle,
  resetArticle,
} from "../../Redux/Slice/DataSlice";
import { useNavigate } from "react-router-dom";
import { StyledDownloadButton, StyledMainCol, StyledQuill } from "./styles";
import TextArea from "antd/es/input/TextArea";
import { StyledContent1 } from "../NewsList/Components/ArticleSidebar/style";
import { StyledPrimaryBackButton, StyledPrimaryButton } from "./styles";
import {
  CaretRightOutlined,
  DownOutlined,
  ExportOutlined,
  FileTextFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { useKeycloak } from "@react-keycloak/web";
import NoPermission from "../../NoPermission";
import { Quill } from "react-quill";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const { Content } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export const ArticleGen = () => {
  const dispatch = useDispatch();
  const [articleText, setArticleText] = useState("");

  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [formattedText, setFormattedText] = useState("");
  const navigate = useNavigate();
  const screens = useBreakpoint(); // Hook to track screen size
  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleDrawerOpen = () => {
    setDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
  };
  const { keycloak } = useKeycloak();

  const {
    response: generatedArticle = [],
    initialArticleLoad: articleLoading,
    loading,
  } = useSelector((state: any) => state.data.generatedArticle);

  const antIcon = (
    <LoadingOutlined
      style={{ fontSize: 30, color: "#005461", fontWeight: 600 }}
      spin
    />
  );

  const prevArticleText = useRef(""); // Track previous text
  const timeoutRef = useRef(null); // Timeout to detect final article update

  useEffect(() => {
    if (generatedArticle && generatedArticle !== "null") {
      setArticleText(generatedArticle); // Stream updates into state
    }
  }, [generatedArticle]);

  useEffect(() => {
    // Clear any previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout: If the article text doesn't change for 2 seconds, dispatch
    timeoutRef.current = setTimeout(() => {
      if (
        prevArticleText.current !== articleText && // Text changed
        articleText.length > 50 && // Ensure article isn't empty (adjust this threshold)
        keycloak.authenticated &&
        keycloak.token
      ) {
        console.log("Article streaming complete!");
      }
      prevArticleText.current = articleText; // Save current text as "last known"
    }, 2000); // Wait 2s to ensure the streaming is done

    return () => clearTimeout(timeoutRef.current); // Cleanup on unmount
  }, [articleText, keycloak.authenticated, keycloak.token, dispatch]);

  const selectedNews = useSelector((state: any) => state.news.selectedArticles);
  
  // Get selected language from localStorage (set by NewsList toggle)
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    localStorage.getItem("selectedLanguage") || "English"
  );

  // Helper utilities for RTL languages (Arabic / Urdu)
  const isRtlLanguage = (lang?: string) => {
    if (!lang) return false;
    const lower = lang.toLowerCase();
    return (
      lower.startsWith("ar") ||
      lower === "arabic" ||
      lower.startsWith("ur") ||
      lower === "urdu" ||
      lower.startsWith("pa") ||
      lower === "pashto"
    );
  };

  const wrapInRtlContainer = (html: string) =>
    `<div style="direction: rtl; text-align: right; font-family: Arial;">${html}</div>`;

  useEffect(() => {
    if (generatedArticle && generatedArticle !== "null") {
      setArticleText(generatedArticle); // Update articleText with the new generated data
    }
  }, [generatedArticle]);

  useEffect(() => {
    if (
      generatedArticle &&
      generatedArticle !== "null" &&
      isRtlLanguage(selectedLanguage)
    ) {
      setFormattedText(
        wrapInRtlContainer(generatedArticle)
      );
    }
  }, [generatedArticle, selectedLanguage]);
  
  // Update selectedLanguage from localStorage when it changes
  useEffect(() => {
    const handleStorageChange = () => {
      const storedLanguage = localStorage.getItem("selectedLanguage") || "English";
      setSelectedLanguage(storedLanguage);
    };
    
    // Listen for storage changes
    window.addEventListener("storage", handleStorageChange);
    
    // Also check on mount and periodically (for same-tab updates)
    const interval = setInterval(() => {
      const storedLanguage = localStorage.getItem("selectedLanguage") || "English";
      if (storedLanguage !== selectedLanguage) {
        setSelectedLanguage(storedLanguage);
      }
    }, 100);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [selectedLanguage]);
  
  // Set Quill editor direction based on selected language
  useEffect(() => {
    const quillEditor = document.querySelector("#quill-content .ql-editor");
    if (quillEditor) {
      const isRtl = isRtlLanguage(selectedLanguage);
      quillEditor.setAttribute("dir", isRtl ? "rtl" : "ltr");
      (quillEditor as HTMLElement).style.textAlign = isRtl ? "right" : "left";
    }
  }, [selectedLanguage]);

  const handleChange = (value) => {
    setArticleText(value);
    setFormattedText(value);
  };

  const Font = Quill.import("formats/font");
  Font.whitelist = [
    "Arial",
    "Times New Roman", 
    "Courier New",
    "Georgia",
    "Verdana",
    "Helvetica",
    "sans-serif",
    "serif",
    "monospace",
  ];
  Quill.register(Font, true);

  const handleBack = () => {
    setShowGenerate(false);
    dispatch(getNewsDataSuccess(""));
    setEditedText("");
    dispatch(resetArticle());
    setArticleText("");
    localStorage.removeItem("artText");
    navigate("/NewsList");
  };

  const handleBackWithWarning = () => {
    const confirmLeave = window.confirm("Any changes made will be reverted!");
    if (confirmLeave) {
      setUnsavedChanges(false);
      // dispatch(getDataNull());
      // dispatch(nullConfig());
      handleBack();
    }
  };

  const handleBrowserBackButton = (event) => {
    if (unsavedChanges) {
      event.returnValue = "Any changes made will be reverted!";
    }
  };

  useEffect(() => {
    window.onbeforeunload = handleBrowserBackButton;

    return () => {
      window.onbeforeunload = null;
    };
  }, [unsavedChanges]);

  useEffect(() => {
    const savedText = localStorage.getItem("artText");
    if (savedText) {
      setArticleText(savedText);
      setFormattedText(savedText);
    }
  }, []);

  useEffect(() => {
    if (`${articleText}`) {
      localStorage.setItem("artText", `${articleText}`);
    } else if (`${formattedText}`) {
      localStorage.setItem("formateText", `${formattedText}`);
    }
  }, [`${articleText}`, `${formattedText}`]);

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [textareaValue, setTextareaValue] = useState("");

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setTextareaValue(value);
    setIsButtonDisabled(value.trim() === "");
  };
  const [form] = Form.useForm();

  const handleClick = () => {
    const promptText = form.getFieldValue("Prompt");
    // Normalize language: API returns "Arabic" or "English" (capitalized)
    // Convert to lowercase for API: "english", "arabic", or "urdu"
    const normalizeLanguage = (lang?: string) => {
      if (!lang) return "english";
      const lower = lang.toLowerCase();
      if (lower.startsWith("ar")) return "arabic";
      if (lower.startsWith("ur")) return "urdu";
      if (lower.startsWith("pa")) return "pashto";
      return "english";
    };
    
    const language = normalizeLanguage(selectedLanguage);
    const payload = {
      promptString: promptText,
      article: articleText,
      language: language, // Ensure the generated article is sent as a string
    };

    dispatch(getReGenerateArticle({ payload, token: keycloak.token }));

    setIsButtonDisabled(true);
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: Font.whitelist }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      ["blockquote", "code-block"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [{ direction: "rtl" }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "blockquote",
    "code-block",
    "list",
    "bullet",
    "indent",
    "direction",
    "align",
    "link",
    "image",
    "video",
  ];

  const hasNoTextTokens = false; // Always allow generation

  const handleExportPDF = async () => {
    try {
      message.loading("Generating PDF...", 0);
      
      const sourceQuill = document.querySelector("#quill-content .ql-editor");
      const exportContainer = document.getElementById("hidden-quill-export");
      const isRtl = isRtlLanguage(selectedLanguage);

      if (sourceQuill && exportContainer) {
        const exportHtml = isRtl
          ? wrapInRtlContainer(sourceQuill.innerHTML)
          : sourceQuill.innerHTML;

        exportContainer.innerHTML = exportHtml;
        exportContainer.style.direction = isRtl ? "rtl" : "ltr";
        exportContainer.style.textAlign = isRtl ? "right" : "left";

        const canvas = await html2canvas(exportContainer, {
          scale: 2,
          useCORS: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "pt", "a4");
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save("Article.pdf");
        
        message.destroy();
        message.success("PDF downloaded successfully!");
      } else {
        message.destroy();
        message.error("An error occurred");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.destroy();
      message.error("An error occurred");
    }
  };

  const downloadAsWord = () => {
    try {
      message.loading("Generating Word document...", 0);
      
      const content = isRtlLanguage(selectedLanguage)
        ? wrapInRtlContainer(articleText)
        : articleText;

      const blob = new Blob(
        [
          `<html><head><meta charset='utf-8'></head><body>${content}</body></html>`,
        ],
        {
          type: "application/msword",
        }
      );

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "Article.doc";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.destroy();
      message.success("Word document downloaded successfully!");
    } catch (error) {
      console.error("Error generating Word document:", error);
      message.destroy();
      message.error("An error occurred");
    }
  };

  const handleExportToCMS = async () => {
    try {
      const content = isRtlLanguage(selectedLanguage)
        ? wrapInRtlContainer(articleText)
        : articleText;
      
      message.loading("Workflow has started...", 0);
      
      const response = await fetch("https://karobarbepesh.smartdemo.live/peshawar-process-article", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: content,
        }),
      });

      message.destroy();

      if (response.ok) {
        message.success("Article exported to CMS successfully!");
      } else {
        message.error("An error occurred");
      }
    } catch (error) {
      console.error("Error exporting to CMS:", error);
      message.destroy();
      message.error("An error occurred");
    }
  };

  const exportMenu = (
    <Menu
      onClick={({ key }) => {
        if (key === "pdf") {
          handleExportPDF();
        } else if (key === "word") {
          downloadAsWord();
        }
      }}
    >
      <Menu.Item key="pdf">Save as PDF</Menu.Item>
      <Menu.Item key="word">Save as Word</Menu.Item>
    </Menu>
  );
  return keycloak.authenticated ? (
    <StyledMainCol lg={{ span: 18, offset: 3 }}>
      <Row
        justify={"space-between"}
        style={{ minHeight: "100%", borderRadius: "5px" }}
      >
        <Col lg={15} xs={24}>
          <Content
            style={{
              minHeight: "100%",
              maxHeight: "100%",
              background: "white",
              borderRadius: "10px",
              borderRight: "10px solid whitesmoke",
              marginLeft: !screens.lg && !screens.xl && !screens.xxl ? "10px" : "0",
            }}
          >
            {!screens.lg && !screens.xl && !screens.xxl && (
              <StyledPrimaryButton
                style={{
                  display: "block",
                  marginBottom: "5px",
                  borderRadius: "5px",
                  width: "100%",
                }}
                type="primary"
                onClick={handleDrawerOpen}
              >
                Regenerate
              </StyledPrimaryButton>
            )}

            <Row
              align={"middle"}
              style={{ borderBottom: "0.5px solid lightgrey", height: "60px" }}
            >
              <Title
                style={{
                  fontSize: "18px",
                  marginLeft: "20px",
                  display: "flex",
                  alignItems: "center",
                  color: "#005461",
                }}
              >
                <FileTextFilled
                  style={{
                    fontSize: "14px",
                    marginRight: "5px",
                    color: "#005461",
                  }}
                />
                Generated Article
              </Title>
            </Row>
            <Row
              style={{ width: "100%", marginTop: "30px", minHeight: "100%" }}
            >
              {articleLoading ? (
                <Content style={{ marginTop: "250px" }}>
                  <Row justify={"center"} align={"middle"}>
                    <Spin style={{ fontSize: "30px" }} indicator={antIcon} />
                    <Text
                      style={{
                        fontSize: "30px",
                        marginLeft: 10,
                        color: "#005461",
                        fontFamily: "Lato",
                        fontWeight: 600,
                      }}
                    >
                      Generating Article
                    </Text>
                  </Row>
                </Content>
              ) : (
                <Col span={24}>
                  <div
                    id="quill-content"
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      border: "1px solid #e1e8ed",
                      overflow: "hidden",
                    }}
                  >
                    <StyledQuill
                      value={
                        isRtlLanguage(selectedLanguage)
                          ? `${formattedText}`
                          : `${articleText}`
                      }
                      onChange={handleChange}
                      modules={modules}
                      formats={formats}
                      theme="snow"
                      defaultValue="<p style='font-family: Arial;'>Start typing...</p>"
                    />
                  </div>
                </Col>
              )}
            </Row>
          </Content>
        </Col>

        <Col
          lg={9}
          xs={0}
          style={{
            minHeight: "100%",
            background: "white",
            borderRadius: "5px",
          }}
        >
          <Row justify={"center"}>
            <Content style={{ borderBottom: "1px solid lightgrey", width: "100%", padding: "16px" }}>
              <Title style={{ fontSize: "20px", color: "#005461" }}>
                Article Regeneration:
              </Title>
              <Text style={{ color: "red" }}>
                Note: Please provide a relevant prompt for accurate
                generation.
              </Text>
              <Form form={form} layout="vertical">
                <Form.Item
                  style={{ marginTop: "5px", marginBottom: "5px" }}
                  name="Prompt"
                >
                  <TextArea
                    rows={2}
                    style={{ fontFamily: "Inter" }}
                    placeholder="Write a prompt to regenrate the article:"
                    maxLength={2000}
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    onChange={handleDescriptionChange}
                  />
                </Form.Item>
                <Content
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    backgroundColor: "white",
                  }}
                >
                  <StyledPrimaryButton
                    style={{ marginTop: "0px", marginBottom: "15px" }}
                    type="primary"
                    onClick={() => {
                      if (hasNoTextTokens) {
                        message.error(
                          "You do not have enough text tokens"
                        );
                        return;
                      }

                      handleClick();
                    }}
                    disabled={isButtonDisabled || articleLoading}
                  >
                    Regenerate
                  </StyledPrimaryButton>
                </Content>
              </Form>
            </Content>

            <Content
              style={{
                height: "100%",
                transition: "width 0.3s ease-in-out",
                background: "white",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                padding: "16px",
              }}
            >
              {/* Export Buttons Section */}
              <Content style={{ marginBottom: "20px" }}>
                <Text
                  style={{
                    color: "#005461",
                    fontSize: "18px",
                    fontWeight: "600",
                    fontFamily: "Lato",
                    marginBottom: "15px",
                    display: "block"
                  }}
                >
                  Export Options:
                </Text>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <Dropdown overlay={exportMenu} disabled={articleLoading}>
                    <StyledDownloadButton
                      type="primary"
                      style={{ width: "100%" }}
                    >
                      <ExportOutlined /> Save As <DownOutlined />
                    </StyledDownloadButton>
                  </Dropdown>
                  <StyledDownloadButton
                    onClick={handleExportToCMS}
                    disabled={articleLoading}
                    type="primary"
                    style={{ 
                      backgroundColor: "#b80000", 
                      borderColor: "#b80000",
                      width: "100%"
                    }}
                  >
                    <ExportOutlined /> Export to CMS
                  </StyledDownloadButton>
                </div>
              </Content>

              {/* Back Button */}
              <StyledContent1
                style={{
                  marginRight: 0,
                  marginTop: "auto",
                  paddingTop: "20px",
                  borderTop: "1px solid lightgrey"
                }}
              >
                <StyledPrimaryBackButton
                  style={{
                    width: "100%",
                  }}
                  type="primary"
                  onClick={handleBackWithWarning}
                  disabled={articleLoading}
                >
                  Go back
                </StyledPrimaryBackButton>
              </StyledContent1>
            </Content>
          </Row>
        </Col>

        <Drawer
          title="Write a Prompt"
          placement="right"
          onClose={handleDrawerClose}
          visible={drawerVisible}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label={
                <span
                  style={{
                    color: "#005461",
                    fontSize: "16px",
                    fontWeight: "600",
                    fontFamily: "Lato",
                  }}
                >
                  Prompt
                </span>
              }
              name="Prompt"
            >
              <TextArea
                rows={8}
                style={{ fontFamily: "Inter" }}
                placeholder="Enter the prompt here:"
                maxLength={2000}
                autoSize={{ minRows: 10, maxRows: 10 }}
                onChange={handleDescriptionChange}
              />
            </Form.Item>
          </Form>
          <div style={{ marginBottom: "20px" }}>
            <Text
              style={{
                color: "#005461",
                fontSize: "16px",
                fontWeight: "600",
                fontFamily: "Lato",
                marginBottom: "10px",
                display: "block"
              }}
            >
              Export Options:
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
              <Dropdown overlay={exportMenu} disabled={articleLoading}>
                <StyledDownloadButton
                  type="primary"
                  style={{ width: "100%" }}
                >
                  <ExportOutlined /> Save As <DownOutlined />
                </StyledDownloadButton>
              </Dropdown>
              <StyledDownloadButton
                onClick={handleExportToCMS}
                disabled={articleLoading}
                type="primary"
                style={{ 
                  backgroundColor: "#b80000", 
                  borderColor: "#b80000",
                  width: "100%"
                }}
              >
                <ExportOutlined /> Export to CMS
              </StyledDownloadButton>
            </div>
          </div>

          <StyledContent1
            style={{
              marginRight: 0,
            }}
          >
            <StyledPrimaryBackButton
              style={{
                marginRight: "2%",
              }}
              type="primary"
              onClick={handleBackWithWarning}
              disabled={articleLoading}
            >
              Go back
            </StyledPrimaryBackButton>

            <StyledPrimaryButton
              style={{ width: "50%" }}
              type="primary"
              onClick={() => {
                if (hasNoTextTokens) {
                  message.error("You do not have enough text tokens");
                  return;
                }

                handleClick();
              }}
              disabled={isButtonDisabled || articleLoading}
            >
              Regenerate
            </StyledPrimaryButton>
          </StyledContent1>
        </Drawer>
      </Row>

      <div
        id="hidden-quill-export"
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          width: "800px",
          padding: "20px",
          background: "#fff",
        }}
      ></div>
    </StyledMainCol>
  ) : (
    <NoPermission />
  );
};

export default ArticleGen;
